import React, { useEffect, useState } from "react";
import { useAuth } from "../auth/AuthContext";
import { useAuthStore } from "../stores/useAuthStore";
import { fetchWithAuth } from "../api/fetchWithAuth";
import {
  usePlatformOrgContext,
  writeImpersonation,
} from "../stores/usePlatformOrgContext";
import { useOrgStore } from "../stores/useOrgStore";
import { Settings } from "lucide-react";
import { useAppNav } from '../hooks/useAppNav'
import { ORG_ROLE_LABEL } from "../constants/orgRoles";
import { LICENSE_TIER_LABEL } from "../constants/licenseTiers";

export default function UsersRolesPanel() {
  const { logout } = useAuth();
  const clearAuth = useAuthStore((s) => s.clearAuth);

  // Single source of truth — populated at app bootstrap in main.jsx.
  const org = useOrgStore((s) => s.activeOrg);
  const role = useOrgStore((s) => s.role);
  const tierCode = useOrgStore((s) => s.tierCode);

  const token = window.__keycloak?.tokenParsed;

  const userEmail = token?.email ?? "unknown";
  const userName =
    token?.name ||
    token?.preferred_username ||
    userEmail;

  const isOrgAdmin = role === "org_admin";

  // ── ACTING-ORG PICKER, PLATFORM ADMINS ONLY ─────────────────────────────────
  //
  // This is ELEVATION, not preference: acting as an org you do not belong to.
  // The gate is the platform_admin realm role, read the same way App.jsx:153
  // reads it, and the org list comes from GET /api/v1/platform/orgs, which is
  // already requirePlatformAdmin-gated server-side. The UI gate hides the
  // control; the server gate is what enforces it. A member choosing among orgs
  // they belong to is a different feature with a different authorization — it
  // needs an endpoint that does not exist yet, and it is not this.
  //
  // ?as_org=<uuid> STAYS. It is how impersonation was tested, it works before
  // this panel has rendered, and removing it breaks a working path for nothing.
  // Two activation surfaces writing one store is fine; two stores would not be.
  //
  // NO RELOAD HERE, DELIBERATELY. Changing the acting org must invalidate every
  // org-scoped cache, and that is installOrgContextInvalidation() subscribing to
  // this store — see stores/usePlatformOrgContext.js. This handler's only job is
  // to write the store and the key. A reload at this call site would be a second
  // implementation of a rule that already has one, and the two would part.
  const isPlatformAdmin =
    token?.realm_access?.roles?.includes("platform_admin") === true;
  const actingOrgId = usePlatformOrgContext((s) => s.activeOrgId);
  const setActiveOrg = usePlatformOrgContext((s) => s.setActiveOrg);
  const clearActiveOrg = usePlatformOrgContext((s) => s.clearActiveOrg);
  const [orgs, setOrgs] = useState([]);
  const [orgsError, setOrgsError] = useState(false);

  useEffect(() => {
    if (!isPlatformAdmin) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchWithAuth("/api/v1/platform/orgs");
        if (!res.ok) throw new Error(String(res.status));
        const data = await res.json();
        if (!cancelled) setOrgs(data.orgs || []);
      } catch {
        // A failed list leaves the select empty rather than the panel broken.
        if (!cancelled) setOrgsError(true);
      }
    })();
    return () => { cancelled = true; };
  }, [isPlatformAdmin]);

  function handleActingOrgChange(e) {
    const id = e.target.value;
    const next = orgs.find((o) => o.id === id);
    if (next) {
      writeImpersonation(next.id);
      setActiveOrg(next);
    } else {
      writeImpersonation(null);
      clearActiveOrg();
    }
  }

  const { goMapTab } = useAppNav()

  const handleLogout = () => {
    clearAuth();
    logout();
  };

  return (
    <div className="absolute top-3 right-3 translate-x-[calc(100%+0.75rem)] sm:translate-x-0 sm:static sm:relative pointer-events-none sm:pointer-events-auto">
      <div className="hidden sm:block absolute top-0 right-0 w-64 rounded-xl border border-slate-800 bg-bg-softer/95 backdrop-blur-sm text-xs shadow-lg shadow-black/60 pointer-events-auto">
        <div className="px-3 py-2 border-b border-slate-800">
          <div className="text-[11px] font-semibold text-gray-200">
            User Profile
          </div>
        </div>

        <div className="p-3 space-y-3">
          {/* USER */}
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-fg-secondary">
              Signed in as
            </div>
            <div className="text-[11px] text-gray-200 font-medium">
              {userName}
            </div>
            <div className="text-[10px] text-gray-400">
              {userEmail}
            </div>
          </div>

          {/* ORG */}
          {org && (
            <div className="border-t border-slate-800 pt-2">
              <div className="text-[10px] font-mono uppercase tracking-widest text-fg-secondary">
                Organization
              </div>

              <div className="flex items-center justify-between mt-1">
                <span className="text-[11px] text-gray-200 font-medium">
                  {org.name}
                </span>
                <span className="px-2 py-0.5 rounded-full bg-bg border border-slate-700 text-[10px] font-mono uppercase tracking-[0.14em] text-gray-400">
                  {ORG_ROLE_LABEL[role] ?? "member"}
                </span>
              </div>

              {/* License tier — only show when assigned. Unassigned users
                  (e.g., during invite flow before tier provisioning lands)
                  get a quieter "Unassigned" indicator. */}
              {tierCode ? (
                <div className="flex items-center justify-between mt-1.5">
                  <span className="text-[10px] text-fg-secondary">License</span>
                  <span className="px-2 py-0.5 rounded-full bg-accent-info/10 border border-accent-info/40 text-[10px] font-mono uppercase tracking-[0.14em] text-accent-info">
                    {LICENSE_TIER_LABEL[tierCode] ?? tierCode}
                  </span>
                </div>
              ) : (
                <div className="flex items-center justify-between mt-1.5">
                  <span className="text-[10px] text-fg-secondary">License</span>
                  <span className="text-[10px] text-fg-secondary italic">Unassigned</span>
                </div>
              )}
            </div>
          )}
          {isPlatformAdmin && (
            // ── THIS SECTION MUST NOT BUBBLE ITS CLICKS ────────────────────
            //
            // App.jsx:428 wraps this panel in `onClick={() => setShowUserPanel(false)}`
            // — ANY click inside closes the whole panel. Every control that
            // existed before is a button that finishes its job on that click
            // (Manage Org navigates, Log out leaves), so closing was invisible
            // and often wanted.
            //
            // A <select> is the first control here that needs to SURVIVE a
            // click. Without this the native dropdown opens on mousedown and
            // the panel unmounts on release, so the list appears and vanishes
            // and no option can ever be chosen. Reported from the browser on
            // 2026-09-02, the first time the picker was used.
            //
            // Stopping propagation here rather than deleting the wrapper's
            // handler: that handler is what closes the panel after Manage Org
            // navigates, and removing it would leave the panel sitting over the
            // admin view. Dismissal on an OUTSIDE click is separate and already
            // correct — App.jsx:167 listens for mousedown outside the ref.
            <div
              className="border-t border-slate-800 pt-2"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-[10px] font-mono uppercase tracking-widest text-fg-secondary">
                Acting as
              </div>
              <select
                aria-label="Acting organization"
                className="mt-1 w-full rounded bg-bg border border-slate-700 px-2 py-1 text-[11px] text-gray-200"
                value={actingOrgId || ""}
                onChange={handleActingOrgChange}
              >
                <option value="">{org?.name ?? "My organization"}</option>
                {orgs.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </select>
              {orgsError && (
                <div className="mt-1 text-[10px] text-amber-400">
                  Could not load organizations.
                </div>
              )}
            </div>
          )}
          {isOrgAdmin && (
            <div className="border-t border-slate-800 pt-2">
              <button
                onClick={() => {
                  goMapTab('admin');
                }}
                className="
        w-full flex items-center gap-2
        rounded bg-bg hover:bg-bg-soft
        border border-slate-700
        px-2 py-1
        text-[11px] text-gray-200
      "
              >
                <Settings size={14} className="text-gray-400" />
                <span>Manage Org</span>
              </button>
            </div>
          )}

          {/* ACTIONS */}
          <div className="border-t border-slate-800 pt-2">
            <button
              onClick={handleLogout}
              className="w-full rounded bg-red-900/40 hover:bg-red-900/60 px-2 py-1 text-[11px]"
            >
              Log out
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}